import type {
  AttachmentSize,
  BlueprintDocument,
  DeploymentSettings,
  NetworkSetting,
  Provider,
  JsonObject,
  OnFailure,
  ParameterDefinition,
  ParameterValue,
  Placement,
  VaultMode,
} from '../api/types';
import { providersUsed } from '../lib/blueprint';
import { strings } from '../strings';

/** The Settings tab's sections (spec 6.13), also `?section=`. */
export const SETTINGS_SECTIONS = [
  'general',
  'placement',
  // Feature spec 17: shown while networking is on or the deployment has a network.
  'network',
  'parameters',
  'accounts',
  'vault',
  'failure',
] as const;
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];

export const DEPLOYMENT_NAME = /^[A-Za-z0-9][A-Za-z0-9 _-]{0,63}$/;

/** Editable form state of a deployment's name and settings (create steps, Settings tab); parameters as text. */
export interface SettingsForm {
  name: string;
  defaultProvider: string;
  defaultRegion: string;
  parameters: Record<string, string>;
  /** Values for parameters the blueprint no longer declares; kept unchanged. */
  extraParameters: Record<string, ParameterValue>;
  overrides: Record<string, { provider: string; region: string }>;
  cloudAccounts: Record<string, string>;
  secretStore: string;
  onFailure: OnFailure;
  /** `default`: the vault goes where the default placement is (written explicitly); `custom`: the fields below. */
  vaultPlacementChoice: 'default' | 'custom';
  /**
   * Feature spec 15: the API's `vaultMode` (where secret values live); null leaves it to the server (create's
   * default, or the stored value's fill-in).
   */
  vaultMode: VaultMode | null;
  vaultProvider: string;
  vaultRegion: string;
  /** The loaded settings' `vault` (undefined when creating), so an untouched vault stays as it was. */
  originalVault: Placement | null | undefined;
  /**
   * Feature spec 17: the network the deployment attaches to (null: standalone). `sizes` keys are `<provider>/<region>`;
   * a null `size` leaves it to the network's default.
   */
  network: {
    networkId: string;
    size: AttachmentSize | null;
    sizes: Record<string, AttachmentSize>;
    /** Feature spec 17, phase 2: request ID → target deployment ID. */
    requestTargets?: Record<string, string>;
  } | null;
  /** The loaded settings' `network` (undefined when creating), so an untouched setting is sent back as it was. */
  originalNetwork: NetworkSetting | null | undefined;
}

function samePlacement(a: Placement | null | undefined, b: Placement | null | undefined): boolean {
  return (a?.provider ?? '') === (b?.provider ?? '') && (a?.region ?? '') === (b?.region ?? '');
}

export function toForm(
  name: string,
  settings: DeploymentSettings | undefined,
  definitions: Record<string, ParameterDefinition>,
): SettingsForm {
  const values = settings?.parameters ?? {};
  const vault = settings?.vault;
  const followsDefault = !vault || samePlacement(vault, settings?.defaultPlacement);
  return {
    name,
    defaultProvider: settings?.defaultPlacement?.provider ?? '',
    defaultRegion: settings?.defaultPlacement?.region ?? '',
    parameters: Object.fromEntries(
      Object.keys(definitions).map((key) => [key, values[key] === undefined ? '' : String(values[key])]),
    ),
    extraParameters: Object.fromEntries(Object.entries(values).filter(([key]) => !(key in definitions))),
    overrides: Object.fromEntries(
      Object.entries(settings?.placementOverrides ?? {}).map(([id, p]) => [
        id,
        { provider: p.provider ?? '', region: p.region ?? '' },
      ]),
    ),
    cloudAccounts: { ...(settings?.cloudAccounts ?? {}) },
    secretStore: settings?.secretStore ?? 'default',
    onFailure: settings?.onFailure ?? 'stop',
    vaultPlacementChoice: followsDefault ? 'default' : 'custom',
    vaultMode: settings?.vaultMode ?? null,
    vaultProvider: vault?.provider ?? '',
    vaultRegion: vault?.region ?? '',
    originalVault: settings ? (vault ?? null) : undefined,
    network: settings?.network
      ? {
          networkId: settings.network.networkId,
          size: settings.network.size ?? null,
          sizes: { ...(settings.network.sizes ?? {}) },
          requestTargets: { ...(settings.network.requestTargets ?? {}) },
        }
      : null,
    originalNetwork: settings ? (settings.network ?? null) : undefined,
  };
}

/**
 * The `settings.network` the form stores (feature spec 17): absent when standalone and the loaded settings had none,
 * null to detach, else the setting with what the form changed (the loaded members it doesn't edit are kept, so an
 * untouched form gives an empty patch).
 */
export function formNetwork(form: SettingsForm): NetworkSetting | null | undefined {
  const original = form.originalNetwork ?? null;
  if (!form.network) return original ? null : undefined;
  const result: NetworkSetting = {
    ...(original && original.networkId === form.network.networkId ? original : {}),
    networkId: form.network.networkId,
  };
  if (form.network.size) result.size = form.network.size;
  else if (result.size !== null) delete result.size;
  if (Object.keys(form.network.sizes).length) result.sizes = { ...form.network.sizes };
  else if (result.sizes !== null) delete result.sizes;
  const targets = form.network.requestTargets ?? {};
  if (Object.keys(targets).length) result.requestTargets = { ...targets };
  else if (result.requestTargets !== null) delete result.requestTargets;
  return result;
}

export function parseParameter(definition: ParameterDefinition, text: string): ParameterValue | undefined {
  if (text === '') return undefined;
  if (definition.type === 'number') {
    const value = Number(text);
    return Number.isFinite(value) ? value : undefined;
  }
  if (definition.type === 'boolean') return text === 'true';
  return text;
}

function placement(provider: string, region: string): Placement | null {
  const result: Placement = {};
  if (provider) result.provider = provider;
  if (region) result.region = region;
  return Object.keys(result).length ? result : null;
}

/**
 * The vault placement the form stores (spec 6.4 create step 4, 6.13 item 6): "Same as the default placement"
 * writes the default placement explicitly, except that a loaded `vault: null` stays null (the server fills it
 * from the recorded vault, or it follows the default); a fixed vault keeps what was loaded.
 */
export function formVault(form: SettingsForm, fixed = false): Placement | null {
  if (fixed && form.originalVault !== undefined) return form.originalVault;
  if (form.vaultPlacementChoice === 'custom') return placement(form.vaultProvider, form.vaultRegion);
  if (form.originalVault === null) return null;
  return placement(form.defaultProvider, form.defaultRegion);
}

export function placementSettings(
  form: SettingsForm,
  fixedVault = false,
): Pick<DeploymentSettings, 'defaultPlacement' | 'placementOverrides' | 'vault'> {
  const placementOverrides: Record<string, Placement> = {};
  for (const [id, value] of Object.entries(form.overrides)) {
    const p = placement(value.provider, value.region);
    if (p) placementOverrides[id] = p;
  }
  return {
    defaultPlacement: placement(form.defaultProvider, form.defaultRegion),
    placementOverrides,
    vault: formVault(form, fixedVault),
  };
}

/**
 * The providers a deployment uses: the dry run's when given (`used`), else the form's placements (before the
 * first dry run answered).
 */
export function usedProviders(form: SettingsForm, doc: BlueprintDocument, used?: readonly string[] | null) {
  if (used) return [...new Set(used)].sort();
  return providersUsed(doc, placementSettings(form));
}

export function fromForm(
  form: SettingsForm,
  doc: BlueprintDocument,
  definitions: Record<string, ParameterDefinition>,
  options: { used?: readonly string[] | null; fixedVault?: boolean; allAccounts?: boolean } = {},
): DeploymentSettings {
  const parameters: Record<string, ParameterValue> = { ...form.extraParameters };
  for (const [key, definition] of Object.entries(definitions)) {
    const value = parseParameter(definition, form.parameters[key] ?? '');
    if (value !== undefined) parameters[key] = value;
  }
  const placements = placementSettings(form, options.fixedVault);
  // Creating keeps the accounts of the providers in use; the Settings tab keeps every account it loaded, so an
  // untouched form gives an empty patch.
  const cloudAccounts = options.allAccounts
    ? Object.fromEntries(Object.entries(form.cloudAccounts).filter(([, id]) => id))
    : Object.fromEntries(
        usedProviders(form, doc, options.used)
          .filter((provider) => form.cloudAccounts[provider])
          .map((provider) => [provider, form.cloudAccounts[provider]]),
      );
  return {
    parameters,
    defaultPlacement: placements.defaultPlacement,
    placementOverrides: placements.placementOverrides,
    cloudAccounts,
    secretStore: form.secretStore || 'default',
    onFailure: form.onFailure,
    vault: placements.vault,
    ...(form.vaultMode ? { vaultMode: form.vaultMode } : {}),
    ...(formNetwork(form) !== undefined ? { network: formNetwork(form) } : {}),
  };
}

/** The `{ name, settings }` document the PATCH merge patch and the 412 merge compare. */
export function settingsDocument(name: string, settings: DeploymentSettings): JsonObject {
  return { name, settings: settings as unknown as JsonObject };
}

export type FormErrors = Record<string, string[]>;

/** Light client-side checks of the parameters, keyed by JSON Pointer into the request body. */
export function checkParameters(
  form: SettingsForm,
  definitions: Record<string, ParameterDefinition>,
  errors: FormErrors = {},
): FormErrors {
  const add = (pointer: string, message: string) => (errors[pointer] ??= []).push(message);
  for (const [key, definition] of Object.entries(definitions)) {
    const text = form.parameters[key] ?? '';
    const pointer = `/settings/parameters/${key}`;
    if (text === '') {
      if (definition.default === undefined) add(pointer, strings.settings.parameterRequired);
      continue;
    }
    if (definition.type === 'number') {
      const value = Number(text);
      if (!Number.isFinite(value)) add(pointer, strings.settings.parameterRequired);
      else if (
        (definition.minimum !== undefined && value < definition.minimum) ||
        (definition.maximum !== undefined && value > definition.maximum)
      ) {
        add(pointer, strings.settings.range(definition.minimum, definition.maximum));
      }
    }
  }
  return errors;
}

export function checkName(form: SettingsForm, errors: FormErrors = {}): FormErrors {
  if (!DEPLOYMENT_NAME.test(form.name)) (errors['/name'] ??= []).push(strings.settings.nameInvalid);
  return errors;
}

export function checkAccounts(
  form: SettingsForm,
  doc: BlueprintDocument,
  used?: readonly string[] | null,
  errors: FormErrors = {},
): FormErrors {
  for (const provider of usedProviders(form, doc, used)) {
    if (!form.cloudAccounts[provider])
      (errors[`/settings/cloudAccounts/${provider}`] ??= []).push(strings.settings.missingAccount(provider));
  }
  return errors;
}

/** Every light client-side check, keyed by JSON Pointer into the request body. */
export function checkForm(
  form: SettingsForm,
  doc: BlueprintDocument,
  definitions: Record<string, ParameterDefinition>,
  used?: readonly string[] | null,
): FormErrors {
  const errors: FormErrors = {};
  checkName(form, errors);
  checkParameters(form, definitions, errors);
  checkAccounts(form, doc, used, errors);
  return errors;
}

/** A changed field of `{ name, settings }` in words, for the save bar and conflict messages (spec 6.13). */
export function fieldLabel(path: string): string {
  const f = strings.deploymentSettings.fieldNames;
  const parts = path
    .split('/')
    .slice(1)
    .map((p) => p.replace(/~1/g, '/').replace(/~0/g, '~'));
  if (parts[0] === 'name') return f.name;
  const [, group, key, field] = parts;
  switch (group) {
    case 'defaultPlacement':
      return key === 'provider' ? f.defaultProvider : f.defaultRegion;
    case 'parameters':
      return f.parameter(key ?? '');
    case 'placementOverrides':
      return f.override(key ?? '', field ?? 'placement');
    case 'cloudAccounts':
      return f.account(key ?? '');
    case 'vault':
      return key === 'provider' ? f.vaultProvider : f.vaultRegion;
    case 'vaultMode':
      return strings.vaults.modeLegend;
    case 'network':
      return strings.networks.deployment.fieldName;
    case 'secretStore':
      return f.secretStore;
    case 'onFailure':
      return f.onFailure;
    default:
      return parts.join('.');
  }
}

/**
 * The server's message of the provider's first managed-vault limitation the blueprint hits (feature spec 15 5.3):
 * a resource of the limitation's type whose property at its pointer references a secret. `<id>` names the resource.
 */
export function limitationMessage(provider: Provider | undefined, doc: BlueprintDocument): string | null {
  for (const limitation of provider?.managedVaultLimitations ?? []) {
    for (const resource of doc.resources) {
      if (resource.type !== limitation.type) continue;
      let node: unknown = resource.properties;
      for (const token of limitation.pointer.split('/').slice(1)) {
        node = node && typeof node === 'object' ? (node as Record<string, unknown>)[token] : undefined;
      }
      if (typeof node === 'string' && node.includes('${secret.'))
        return limitation.message.replace('<id>', resource.id);
    }
  }
  return null;
}
